import type { AttributeComparisonItem } from '~/components/common/attribute-comparison-list';

import type { MembershipHeaderStats } from '../components/batch-detail/insight-membership-drawer';
import type { InsightMembershipGroupFilter } from '../hooks/use-insight-membership-products';
import type { InsightMembershipCategory } from '../types/catalog-operations.api.types';
import type { BatchInsights, PerformanceTermItem } from '../types/catalog-operations.types';

/** URL-safe enums for the drawer's return trip — the product detail page carries
 *  these back to the batch page so it can re-open the same drawer on return. */
export const MEMBERSHIP_CATEGORIES = [
  'POPULAR_TERM',
  'OCCASION',
  'POPULAR_ATTRIBUTE',
  'MACRO_TREND',
  'MICRO_TREND',
] as const satisfies readonly InsightMembershipCategory[];
export const MEMBERSHIP_GROUPS = [
  'ALL',
  'NEWLY_ADDED',
  'CURRENTLY_SHOWN',
  'REMOVED',
] as const satisfies readonly InsightMembershipGroupFilter[];

/** Which drawer (row + chip) to re-open when returning from a product. */
export interface MembershipReturnState {
  category: InsightMembershipCategory;
  label: string;
  group?: InsightMembershipGroupFilter | undefined;
}

/** Header stats for a Product Term row. */
export function buildTermHeaderStats(item: PerformanceTermItem): MembershipHeaderStats {
  return {
    kind: 'term',
    monthlySearches: item.monthlySearchCount,
    competition: item.competition,
    countBefore: item.terms.beforeCount,
    countAfter: item.terms.afterCount,
  };
}

/** Header stats for an Occasion row — `null` when the row predates the raw
 *  counts, so the drawer opens without a stat strip. */
export function buildOccasionHeaderStats(
  item: AttributeComparisonItem,
): MembershipHeaderStats | null {
  return item.countBefore != null && item.countAfter != null
    ? {
        kind: 'occasion',
        coverageBefore: item.beforeValue,
        coverageAfter: item.afterValue,
        countBefore: item.countBefore,
        countAfter: item.countAfter,
      }
    : null;
}

/** Header stats for a Macro/Micro trend row (its before/after values are raw counts). */
export function buildTrendHeaderStats(
  item: AttributeComparisonItem,
  trendKind: 'macro' | 'micro',
): MembershipHeaderStats {
  return { kind: 'trend', trendKind, countBefore: item.beforeValue, countAfter: item.afterValue };
}

/** Rebuilds a drawer's header stats from the loaded insights by row label — used
 *  when the drawer is restored from the URL, where no clicked row is in hand. */
export function findMembershipHeaderStats(
  category: InsightMembershipCategory,
  label: string,
  insights: BatchInsights | undefined,
): MembershipHeaderStats | null {
  if (!insights) return null;
  if (category === 'POPULAR_TERM') {
    const term = insights.popularTerms.find((item) => item.term === label);
    return term ? buildTermHeaderStats(term) : null;
  }
  if (category === 'OCCASION') {
    const occasion = insights.occasions.find((item) => item.title === label);
    return occasion ? buildOccasionHeaderStats(occasion) : null;
  }
  if (category === 'MACRO_TREND' || category === 'MICRO_TREND') {
    const trendKind = category === 'MACRO_TREND' ? 'macro' : 'micro';
    const trend = insights.trends.find(
      (item) => item.title === label && item.trendKind === trendKind,
    );
    return trend ? buildTrendHeaderStats(trend, trendKind) : null;
  }
  return null;
}
